/* The Inbox's rules — who a coach has a thread with, what is unread, and what may be sent.
 *
 * `docs/EVERFIT-MAP.md` §32.4 tier 1 #3 is the one row on that list where we are measurably
 * WORSE than the product coaches already complain about: the transport exists, the table exists,
 * the phone screens exist, and the web had a placeholder. §11.3 and §32.4 disagree about every
 * other priority and agree about this one.
 *
 * PURE on purpose — no Supabase, no React — so every rule below is executed by
 * `messages.test.ts` rather than reasoned about. The Swift harness cannot run TypeScript, so
 * `npm test` is the executed half and `b4-2-the-client-page`'s ratchet is what refuses to let a
 * rule here ship without a runner.
 *
 * ── WHAT THIS FILE DELIBERATELY DOES NOT DECIDE ─────────────────────────────────────────────
 *
 * ⚠️ WHETHER A MESSAGE MAY BE SENT IS THE DATABASE'S ANSWER, NOT THIS FILE'S. The INSERT policy
 * on `coach_messages` requires an ACTIVE `coach_clients` row for exactly this pair, so a thread
 * whose relationship has ended is readable and refuses new messages with no client-side rule to
 * remember (D-V8.2). `composerState` below mirrors that so the coach gets a SENTENCE instead of
 * a database error — it does not enforce it, and it must never be the only thing that stops a
 * send.
 *
 * ⚠️ AND NOTHING HERE SCANS A MESSAGE FOR RED FLAGS, WHICH IS DERIVED RATHER THAN OMITTED.
 * `ChatTransport.scansOutgoingForRedFlags` is `readerId == clientId` — a property of the
 * SENDER'S SIDE, because the advisory exists for a person describing their own body and not for
 * whoever is answering them. On this surface the sender is always the COACH, so the scan is
 * false by construction. B-2's `RedFlagWords` (and its Greeklish normaliser, which was silent on
 * 11 of 11 real phrasings before it shipped) stays the athlete's, on the athlete's device, where
 * the message is composed.
 */

import { displayName } from "./roster";
import { dayKey, dayKeyOf } from "./days";
import { WEEKDAYS, realDay } from "./weekLine";
import { dayMonth } from "./thisWeekBoard";
import { foldLine, foldReviews, type Folded } from "./reviewBody";

// ── The rows this file is given ─────────────────────────────────────────────────────────────

/** One `coach_messages` row, as PostgREST returns it. */
export type MessageRow = {
  id: string;
  coachId: string;
  clientId: string;
  senderId: string;
  body: string;
  /** ISO 8601, `timestamptz`. */
  createdAtISO: string;
  /** Stamped by the RECIPIENT — the UPDATE policy carries `sender_id <> auth.uid()`. */
  readAtISO: string | null;
  /** W72 step 6 — the message this one quotes (`coach_messages.reply_to`, same thread by trigger). */
  replyTo?: string | null;
  /** W72 step 6 — a voice note's object, `<sender>/<id>.m4a` (CHECKed), and its length in seconds. */
  audioPath?: string | null;
  audioSeconds?: number | null;
  /** W82 step 4 — `message` | `review` | `comment` (absent on a row read without the column: a message), and the finished
   *  workout a review or comment is about (NULL after the athlete reset it). The fold (`reviewBody.foldReviews`) reads both. */
  kind?: string | null;
  sessionId?: string | null;
  /** His 10-10 ask (2) — a round video (`<sender>/<id>.mp4`, with its seconds) and a photo (`<sender>/<id>.jpg`) in
   *  `coach-review-media`; a chat message may carry them (the + on the Inbox composer), its words then the caption. */
  videoPath?: string | null;
  videoSeconds?: number | null;
  photoPath?: string | null;
};

/** One `coach_clients` row, reduced to what a thread list needs. */
export type ThreadLink = {
  /** Null until an invitation is accepted. Such a link has nobody to talk to. */
  clientId: string | null;
  /** `coach_clients.status` — `pending` | `active` | `declined` | `ended`. */
  status: string;
  firstName: string | null;
  lastName: string | null;
  invitedFirstName: string | null;
  invitedLastName: string | null;
  invitedEmail: string | null;
};

/** One row of the coach's inbox. */
export type ThreadRow = {
  clientId: string;
  name: string;
  /** One line, already collapsed. Empty when nobody has written. */
  preview: string;
  /** Null for a thread nobody has written in — which is not the same as an old one. */
  lastAtISO: string | null;
  /** Messages FROM the client that this coach has not stamped. */
  unread: number;
  /**
   * True when this thread has messages but the person is no longer on the roster.
   *
   * D-V8.2 keeps an ended relationship READABLE, so hiding the row would make that promise
   * unreachable — and a coach who cannot find what was said is worse off than one who can see it
   * is closed. `composerState` is what refuses the send.
   */
  isFormerClient: boolean;
  /**
   * W72 step 6 — the LAST message in the thread is the client's, so the coach owes a reply.
   * ⚠️ DERIVED FROM THE LAST SENDER, NEVER FROM `read_at`: a coach who has READ a message and not
   * answered it is exactly who this chip exists for, and "unread" cannot see them.
   */
  awaiting: boolean;
};

// ── The body's bounds, and they are a copy ──────────────────────────────────────────────────

/**
 * ⚠️ THESE TWO NUMBERS ARE A COPY OF `coach_messages_body_length` IN
 * `20260828040126_v8_coach_messages.sql`, AND THE GATE COMPARES THEM ON EVERY RUN. The migration
 * is the enforcement; this is so the coach is told "that is too long" by the composer instead of
 * by a constraint violation they cannot read. Two halves naming one shape differently is this
 * codebase's defining bug — the difference here is that drifting UPWARD would produce a message
 * the composer accepts and the database refuses, with the coach's typing lost.
 */
export const bodyMinLength = 1;
export const bodyMaxLength = 4000;

// ── Rules ───────────────────────────────────────────────────────────────────────────────────

/**
 * One line of preview for a thread list.
 *
 * Newlines collapse to spaces: a multi-line message otherwise renders as its FIRST LINE ONLY in
 * a one-line row, which silently drops the half a coach is scanning for. Capping is the list's
 * job visually, but the string is bounded here so a 4 000-character message cannot be handed to
 * the DOM as a single row's text.
 */
export function preview(body: string, cap = 120): string {
  const flat = body.replace(/\s+/gu, " ").trim();
  return flat.length <= cap ? flat : flat.slice(0, cap - 1).trimEnd() + "…";
}

/**
 * Who a message should notify: the party that is NOT the sender.
 *
 * ⚠️ IT RETURNS NULL FOR A SENDER WHO IS NEITHER PARTY, AND THAT GUARD IS FOR THE TYPE RATHER
 * THAN FOR THE DATA. `coach_messages_sender_is_a_party` makes such a row unrepresentable in the
 * database, so nothing this app reads can reach the null arm — exactly the distinction
 * `training.ts` draws about its own null clause: unreachable from the DATA, reachable through
 * the TYPE. Collapsing it to "the coach unless the sender is the coach" would be one character
 * shorter and would notify the wrong person the day anything writes such a row.
 */
export function recipientOf(m: Pick<MessageRow, "coachId" | "clientId" | "senderId">): string | null {
  if (m.senderId === m.coachId) return m.clientId;
  if (m.senderId === m.clientId) return m.coachId;
  return null;
}

/** Messages the reader has not stamped that came from the other party. */
export function unreadFor(messages: readonly MessageRow[], readerId: string): number {
  return messages.filter((m) => m.senderId !== readerId && m.readAtISO === null).length;
}

/**
 * The ids a read receipt covers.
 *
 * ⚠️ IT MIRRORS THE UPDATE POLICY'S OWN `sender_id <> auth.uid()` RATHER THAN RELYING ON IT.
 * The policy is what ENFORCES the rule; naming it here keeps a request from silently matching
 * zero rows for a reason the caller cannot see — which is what `CoachMessagingService` does on
 * the phone, for the same reason, in the same words.
 */
export function markableIds(messages: readonly MessageRow[], readerId: string): string[] {
  return messages
    .filter((m) => m.senderId !== readerId && m.readAtISO === null)
    .map((m) => m.id);
}

/**
 * Assemble the coach's inbox.
 *
 * ⚠️ IT IS ROSTER-DRIVEN AND NOT MESSAGE-DRIVEN, AND WITH ZERO MESSAGES IN THE TABLE THAT IS THE
 * DIFFERENCE BETWEEN A FEATURE AND A BLANK PAGE. Building the list from `coach_messages` would
 * show a coach who has never been written to an empty Inbox with no way to start a conversation
 * — measured live on 2026-08-30: `coach_messages` holds 0 rows across 14 profiles, so a
 * message-driven inbox is empty for EVERY user of this app today. Every active client is a
 * thread, written in or not.
 *
 * ⚠️ A PENDING INVITATION GETS NO ROW. There is nobody behind it yet, and the INSERT policy
 * would refuse the message anyway — so a composer against a pending link is a box that sends
 * into the dark. `CoachMessagingService.activeCoach` excludes `pending` on the athlete side for
 * exactly this reason, and the two must agree or one surface offers what the other refuses.
 */
export function threadRows(
  links: readonly ThreadLink[],
  messages: readonly MessageRow[],
  coachId: string,
): ThreadRow[] {
  const rows = new Map<string, ThreadRow>();

  for (const link of links) {
    if (link.status !== "active") continue;
    if (link.clientId === null) continue;
    rows.set(link.clientId, {
      clientId: link.clientId,
      name: nameOf(link),
      preview: "",
      lastAtISO: null,
      unread: 0,
      isFormerClient: false,
      awaiting: false,
    });
  }

  // Newest first, so the first message seen for a thread is its latest. Sorting here rather than
  // trusting the query: neither of this app's message queries carries an `.order()` guarantee
  // the rule can depend on, and session 3 shipped two sorts that were load-bearing in production
  // and invisible in tidy fixtures because every fixture arrived already ordered.
  const byNewest = [...messages].sort((a, b) => (a.createdAtISO < b.createdAtISO ? 1 : a.createdAtISO > b.createdAtISO ? -1 : 0));

  for (const m of byNewest) {
    if (m.coachId !== coachId) continue;
    let row = rows.get(m.clientId);
    if (row === undefined) {
      row = {
        clientId: m.clientId,
        name: "Former client",
        preview: "",
        lastAtISO: null,
        unread: 0,
        isFormerClient: true,
        awaiting: false,
      };
      rows.set(m.clientId, row);
    }
    if (row.lastAtISO === null) {
      row.preview = listPreview(byNewest.filter((x) => x.coachId === coachId && x.clientId === m.clientId));
      row.lastAtISO = m.createdAtISO;
      // Newest first, so this is the LAST message said — the one that decides who owes a reply.
      row.awaiting = m.senderId !== coachId && !row.isFormerClient;
    }
    if (m.senderId !== coachId && m.readAtISO === null) row.unread += 1;
  }

  return inReadingOrder([...rows.values()]);
}

/**
 * Unread first, then most recent activity, then the silent ones by name.
 *
 * ⚠️ THIS IS THE SAME RULE AS `ChatThread.swift`'s `inReadingOrder()`, WRITTEN TWICE, AND THAT
 * IS STATED RATHER THAN HIDDEN. A comparator is logic and not data, so neither of this repo's
 * two compatibility mechanisms reaches it — the generated types cover the MODEL and the
 * generated tokens cover the DESIGN. What makes the duplication tolerable is that the two
 * surfaces disagreeing about ORDER is a presentation difference and not a model divergence: a
 * coach would see the same threads in a different sequence. Nothing here may drift in a way that
 * changes WHICH threads exist or what is unread — those are the halves the rules above own, and
 * they are executed.
 *
 * The ordering itself is the order somebody works an inbox in: what is waiting for a reply,
 * then what happened most recently, then everyone who has said nothing.
 */
export function inReadingOrder(rows: readonly ThreadRow[]): ThreadRow[] {
  return [...rows].sort((a, b) => {
    if ((a.unread > 0) !== (b.unread > 0)) return a.unread > 0 ? -1 : 1;
    if (a.lastAtISO !== null && b.lastAtISO !== null) {
      if (a.lastAtISO === b.lastAtISO) return a.name.localeCompare(b.name);
      return a.lastAtISO > b.lastAtISO ? -1 : 1;
    }
    if (a.lastAtISO !== null) return -1;
    if (b.lastAtISO !== null) return 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  });
}

/** One day's worth of a conversation, in the order it was said. */
export type DayBlock = {
  /** `YYYY-MM-DD`, or null for a message whose timestamp will not parse. */
  dayKey: string | null;
  messages: { message: MessageRow; mine: boolean }[];
};

/**
 * The conversation, oldest first, grouped into days.
 *
 * ⚠️ AN UNPARSEABLE TIMESTAMP GETS ITS OWN BLOCK RATHER THAN JOINING THE PREVIOUS ONE. Grouping
 * it with whatever came before would file a message under a day it was not sent on, which is a
 * confident wrong answer; `dayKey` returning null is reported and the screen renders no date
 * heading for it. `client.ts` takes the same line: an unreadable date is REPORTED, never scored.
 */
export function conversation(messages: readonly MessageRow[], readerId: string): DayBlock[] {
  const oldestFirst = [...messages].sort((a, b) => (a.createdAtISO < b.createdAtISO ? -1 : a.createdAtISO > b.createdAtISO ? 1 : 0));
  const blocks: DayBlock[] = [];
  for (const m of oldestFirst) {
    const key = dayKey(m.createdAtISO);
    const last = blocks[blocks.length - 1];
    if (last !== undefined && last.dayKey === key && key !== null) {
      last.messages.push({ message: m, mine: m.senderId === readerId });
    } else {
      blocks.push({ dayKey: key, messages: [{ message: m, mine: m.senderId === readerId }] });
    }
  }
  return blocks;
}

/** What the composer may do, and why when it may not. */
export type ComposerState =
  | { kind: "open" }
  /** The relationship ended. Reading survives it; sending does not (D-V8.2). */
  | { kind: "relationshipEnded"; reason: string }
  /** There is no relationship at all — a thread that is only history. */
  | { kind: "noRelationship"; reason: string };

/**
 * Whether this thread may be written in.
 *
 * ⚠️ IT NAMES THE REASON RATHER THAN JUST DISABLING THE BOX. A composer that is simply absent is
 * indistinguishable from a screen that is broken, and the coach's next move — ask the client to
 * accept again, or accept that this is history — depends entirely on which case it is. Same rule
 * as the two client panels RLS refuses: say WHICH thing is missing and WHY.
 */
export function composerState(status: string | null): ComposerState {
  if (status === "active") return { kind: "open" };
  if (status === null) {
    return {
      kind: "noRelationship",
      reason: "This person is not on your roster. You can read what was said; you cannot send anything new.",
    };
  }
  return {
    kind: "relationshipEnded",
    reason: `This coaching relationship is ${status}. You can read what was said; sending is closed until it is active again.`,
  };
}

/** Whether a typed body is one the database will accept. */
export type Sendability =
  | { kind: "send"; body: string }
  | { kind: "empty" }
  | { kind: "tooLong"; length: number; max: number };

/**
 * Trim, then judge against the same bounds the CHECK constraint uses.
 *
 * ⚠️ IT TRIMS BEFORE MEASURING BECAUSE THE CONSTRAINT DOES (`char_length(btrim(body))`). A
 * composer that counted the untrimmed string would refuse a message the database would accept,
 * and — worse in the other direction — accept one made only of spaces and have it rejected on
 * the wire with the coach's typing gone.
 */
export function sendability(raw: string): Sendability {
  const body = raw.trim();
  if (body.length < bodyMinLength) return { kind: "empty" };
  if (body.length > bodyMaxLength) return { kind: "tooLong", length: body.length, max: bodyMaxLength };
  return { kind: "send", body };
}

// ── Names ───────────────────────────────────────────────────────────────────────────────────

/**
 * The person's name, through the roster's own rule.
 *
 * ⚠️ `displayName` IS IMPORTED, NOT RESTATED. It already knows that a `coach_clients` row can
 * exist before any profile does — `profiles` has no `email` column, so the fallback runs through
 * `invited_first_name` / `invited_last_name` / `invited_email` — and a second copy of that
 * ladder would disagree with the roster about what to call somebody on two screens of one app.
 */
function nameOf(link: ThreadLink): string {
  // ⚠️ TWO FIELDS, NOT A FABRICATED ROW. This used to build a whole `RosterRow` — a blank link
  // id, a zero assignment count, a null last-trained — purely to reach a name, and every field
  // added to that type since has had to be invented here too. `displayName` asks for what it
  // reads now, so this site states only what it actually knows.
  return displayName({ firstName: link.firstName, lastName: link.lastName }, {
    firstName: link.invitedFirstName,
    lastName: link.invitedLastName,
    email: link.invitedEmail,
  });
}

/* ── W14 · the read receipt, and the three things a sent message can be ──────────────────────── */

/**
 * What to say under a message the COACH sent.
 *
 * 🔴 THE DATA HAS BEEN THERE SINCE 2026-08-28 AND NOTHING HAS EVER DRAWN IT. `coach_messages.read_at`
 * exists with its own column-level UPDATE grant, this file already reads it to COUNT unread, and the
 * conversation printed a bare clock time under every message. Everfit prints `11:37 PM ∙ Seen`, and
 * the map has carried *"ADD — read receipts. (ENGINE, cheap.) the data exists and nothing renders
 * it"* as an open verdict since the crawl.
 *
 * ⚠️ THREE STATES, NOT TWO, AND THE THIRD IS THE ONE THAT MATTERS. "Sent" and "Seen" are the obvious
 * pair; the third is a message still in flight, which must never read as DELIVERED-AND-UNREAD. A
 * coach who sees "Sent" under a message that has not left the browser will send it again.
 *
 * ⚠️ AND NOTHING IS SAID UNDER A MESSAGE THE CLIENT SENT. Whether the COACH has read their own
 * client's message is not news to the coach, and printing "Seen" under somebody else's words
 * invites the reading that the CLIENT saw it — the wrong party entirely.
 */
export type Receipt = "sending" | "sent" | "seen";

export function receiptFor(
  message: { senderId: string; readAtISO: string | null; pending?: boolean },
  coachId: string,
): Receipt | null {
  if (message.senderId !== coachId) return null;
  if (message.pending === true) return "sending";
  return message.readAtISO === null ? "sent" : "seen";
}

/** The word a coach reads. Kept beside the rule so the two cannot drift into different vocabularies. */
export function receiptLabel(r: Receipt): string {
  return r === "sending" ? "Sending…" : r === "sent" ? "Sent" : "Seen";
}

/**
 * W21 (D) — HOW LONG AGO THE LAST MESSAGE LANDED, on the right of an inbox row.
 *
 * 🔴 **THE APP'S OWN STRING CANNOT BE COPIED AND SAYING SO IS THE HONEST ANSWER.** `ChatThreadRow`
 * draws `Text(lastAt, style: .relative)`, which is SwiftUI's own live formatter — it renders
 * `1 hr, 0 min`, it re-renders itself as the clock moves, and it is localised by the system. The
 * web has no equivalent, and hand-writing `1 hr, 0 min` would reproduce a SHAPE nobody chose (the
 * `, 0 min` on a round hour is the formatter's, not a design) while losing the thing that makes
 * it work, which is that it ticks.
 *
 * ⚠️ **SO THIS IS OURS, AND IT IS THE ROSTER'S OWN GRAMMAR RATHER THAN A THIRD ONE.** The one
 * unit that matters, shortest form: `now`, `4 min`, `2 hr`, `3 d`, `5 w`. A coach scanning an
 * inbox needs "recent or not", and a second number after the first is precision nobody reads.
 *
 * ⚠️ **AN UNPARSEABLE OR ABSENT DATE IS AN EMPTY STRING, NEVER `—` OR `now`.** A thread with no
 * messages has no time, and a dash where a clock goes reads as a clock that is broken; the row
 * already says `No messages yet` in its preview, which is the honest half.
 *
 * ⚠️ **A FUTURE TIME IS `now`** — the clock-skew arm the app's roster vocabulary also carries:
 * "in 3 min" about a message that has already arrived is how a column loses its reader.
 */
export function messageAge(iso: string | null, now: Date): string {
  if (iso === null || iso === "") return "";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const mins = Math.floor((now.getTime() - then) / 60000);
  if (mins <= 0) return "now";
  if (mins < 60) return `${String(mins)} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${String(hrs)} hr`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${String(days)} d`;
  return `${String(Math.floor(days / 7))} w`;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * W72 STEP 6 — THE INBOX, PROPOSAL 6 (his yes 2026-09-25 ~00:45). Every shape the screen draws is
 * decided here and EXECUTED by `messages.test.ts`: the day line, who is awaiting a reply, the
 * filters and their empty sentences, the search, the time stamped once per run (the phone's
 * five-minute rule, ported), the one `Seen`, the quote line, the voice note's clocks, the six
 * reactions, and the training that happened IN the conversation's days.
 * ══════════════════════════════════════════════════════════════════════════════════════════════ */

// ── Voice notes ─────────────────────────────────────────────────────────────────────────────

/**
 * THE FIVE-MINUTE CEILING. ⚠️ A COPY OF `coach_messages_audio_seconds` (`between 1 and 300`) IN
 * `20260925160000_coach_messages_reply_and_voice.sql`, compared by the harness gate `w72-6` on every
 * run — the recorder stops HERE so the database never has to refuse a note the coach already made.
 * The phone's `VoiceNote.ceilingSeconds` is the third copy and the gate reads it too.
 */
export const voiceCeilingSeconds = 300;

/** What the body of a voice note says, so every reader that knows only `body` still says what it is. */
export const voiceNoteBody = "🎤 Voice note";

/** `m:ss` — `1:42`, `0:05`, `5:00`. A length that is not a length reads `0:00`, never `NaN:NaN`. */
export function voiceClock(seconds: number | null | undefined): string {
  const s = typeof seconds === "number" && Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  return `${String(Math.floor(s / 60))}:${String(s % 60).padStart(2, "0")}`;
}

/** `0:38 / 1:42` — the position is clamped into the note, so a seek past the end never reads `1:43 / 1:42`. */
export function voiceProgress(position: number, total: number): string {
  const t = Number.isFinite(total) && total > 0 ? total : 0;
  const p = Number.isFinite(position) ? Math.min(Math.max(position, 0), t) : 0;
  return `${voiceClock(p)} / ${voiceClock(t)}`;
}

/**
 * The object's name in `coach-voice` — `<sender>/<message id>.m4a`, LOWERCASE.
 * ⚠️ THE SAME STRING `coach_messages_audio_path_shape` IS CHECKED TO HOLD, and the 2026-09-12
 * lesson is why it lowercases: Postgres renders a uuid lowercase, and a folder that differs by case
 * is refused 42501 by every owner-scoped bucket policy in this project.
 */
export function voicePath(senderId: string, messageId: string): string {
  return `${senderId.toLowerCase()}/${messageId.toLowerCase()}.m4a`;
}

/**
 * The container a browser should record in, or null when it can offer none the PHONE can play.
 * ⚠️ `.m4a` / AAC ONLY: the phone's AVFoundation cannot play WebM/Opus, and the database CHECK
 * refuses any other extension. A browser that can only make WebM gets NO mic and a sentence saying
 * why — never a note that arrives silent on the client's phone.
 */
export const recorderMimeCandidates = ["audio/mp4;codecs=mp4a.40.2", "audio/mp4"] as const;
export function recorderMime(isTypeSupported: (mime: string) => boolean): string | null {
  for (const m of recorderMimeCandidates) if (isTypeSupported(m)) return m;
  return null;
}

/** The speed chip's cycle: 1× → 1.5× → 2× → 1×. Anything else (a stored value from an old build) restarts at 1. */
export const voiceSpeeds = [1, 1.5, 2] as const;
export function nextVoiceSpeed(current: number): number {
  const i = voiceSpeeds.indexOf(current as (typeof voiceSpeeds)[number]);
  return i < 0 ? 1 : voiceSpeeds[(i + 1) % voiceSpeeds.length] ?? 1;
}
export function voiceSpeedLabel(speed: number): string {
  return `${String(speed)}×`;
}

/**
 * `n` bar heights in 0..1 from raw samples — the loudest sample in each slice, scaled by the loudest
 * overall. ⚠️ A SILENT NOTE IS `n` FLAT BARS AT THE FLOOR, NEVER A DIVISION BY ZERO, and a 5-minute
 * note compresses into the same `n` bars as a 5-second one (principle 15: "the waveform compresses,
 * the length reads").
 */
export function waveformBars(samples: ArrayLike<number>, n: number, floor = 0.08): number[] {
  const bars: number[] = [];
  if (n <= 0) return bars;
  const per = samples.length / n;
  let peak = 0;
  for (let i = 0; i < n; i++) {
    let m = 0;
    const from = Math.floor(i * per), to = Math.max(from + 1, Math.floor((i + 1) * per));
    for (let j = from; j < to && j < samples.length; j++) m = Math.max(m, Math.abs(samples[j] ?? 0));
    bars.push(m);
    peak = Math.max(peak, m);
  }
  return bars.map((b) => (peak === 0 ? floor : Math.max(floor, b / peak)));
}

/**
 * One line of preview for a thread row or a quote. A voice note previews as `🎤 Voice note · 1:42`
 * — the length is the one fact the row can carry about a recording.
 */
export function threadPreview(m: Pick<MessageRow, "body" | "audioSeconds">, cap = 120): string {
  if (typeof m.audioSeconds === "number") return `${voiceNoteBody} · ${voiceClock(m.audioSeconds)}`;
  return preview(m.body, cap);
}

/**
 * A thread row's preview: the thread's LAST item read through its own FOLD — a review (with its comments and replies) is its
 * fold line (`Reviewed Thursday's Legs & Core · 2 comments`), anything else `threadPreview`. The phone's
 * `ChatListFacts.preview`, the same rule: before 10-16 the web flattened the review's body, so its head ran into the coach's
 * words (`Reviewed Monday's Lower Good …`). Any order in: it is read NEWEST FIRST, and a tie on the stamp keeps the first, as
 * Swift's `max(by:)` does over the phone's newest-first rows. "" = nothing has been said.
 */
export function listPreview(thread: readonly MessageRow[]): string {
  const newest = [...thread].sort((a, b) => (a.createdAtISO < b.createdAtISO ? 1 : a.createdAtISO > b.createdAtISO ? -1 : 0));
  let last: Folded<MessageRow> | undefined;
  let lastISO = "";
  for (const item of foldReviews(newest)) {
    const iso = item.kind === "review" ? item.latestISO : item.message.createdAtISO;
    if (last === undefined || iso > lastISO) { last = item; lastISO = iso; }
  }
  if (last === undefined) return "";
  return last.kind === "review" ? foldLine(last.review, last.comments.length, last.replies.length) : threadPreview(last.message);
}

// ── The coach's day, in one line ───────────────────────────────────────────────────────────

export type DayLinePart = { kind: "unread" | "awaiting"; count: number; text: string };

/**
 * `2 unread · 1 awaiting your reply`.
 * ⚠️ A COUNT THAT COULD NOT BE READ IS OMITTED — NEVER PRINTED AS 0. `null` means "not known"
 * (the list is still loading, say). A real zero IS printed: it is news that nothing is waiting.
 * 🔴 HIS 2026-10-09 WORD — THE CHAT IS ONLY MESSAGES: the third count (`N trained today`) went with the trained rows. Both
 * counts left are about what was SAID; where a coach sees who trained is the roster, the client page and the Dashboard.
 */
export function dayLine(c: { unread: number | null; awaiting: number | null }): DayLinePart[] {
  const parts: DayLinePart[] = [];
  if (c.unread !== null) parts.push({ kind: "unread", count: c.unread, text: `${String(c.unread)} unread` });
  if (c.awaiting !== null) parts.push({ kind: "awaiting", count: c.awaiting, text: `${String(c.awaiting)} awaiting your reply` });
  return parts;
}

/** The day line's two counts, from the rows the list already holds — one source, never a second query. */
/* Job-3 leftover (2026-10-16): BOTH counts are CONVERSATIONS — the chip counts what pressing it shows (`Unread 2` → two
   rows), `Awaiting reply` can only be counted in conversations, and a coach owes replies to people (three messages from
   one client are one reply). The sidebar badge counts the same thing (`unreadConversations`); it counted messages (3). */
export function inboxCounts(rows: readonly ThreadRow[]): { unread: number; awaiting: number } {
  return {
    unread: rows.filter((r) => r.unread > 0).length,
    awaiting: rows.filter((r) => r.awaiting).length,
  };
}

/** The sidebar badge's number: how many CONVERSATIONS hold something unread — the distinct clients among the unread
 *  rows the shell reads (`client_id` only, no body). The Inbox's `Unread` chip counts the same thing. */
export function unreadConversations(rows: readonly { readonly client_id: string }[]): number {
  return new Set(rows.map((r) => r.client_id)).size;
}

// ── Filters and search ─────────────────────────────────────────────────────────────────────

export type InboxFilter = "all" | "unread" | "awaiting";
export const inboxFilters: readonly { id: InboxFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "unread", label: "Unread" },
  { id: "awaiting", label: "Awaiting reply" },
];

/** Lowercase, accents gone — so `Σπύρος` finds `σπυρος` and `Jose` finds `José`. */
export function searchable(s: string): string {
  return s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
}

/** Every thread's words, once — the name is matched from the row, the messages from here. */
export function searchIndex(messages: readonly MessageRow[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const x of messages) m.set(x.clientId, `${m.get(x.clientId) ?? ""}\n${searchable(x.body)}`);
  return m;
}

/**
 * The rows a chip and the search leave standing, in the order they came (the reading order).
 * One search over the NAME and every MESSAGE of the thread — "Search messages…" means both.
 */
export function filterThreads(
  rows: readonly ThreadRow[],
  filter: InboxFilter,
  query: string,
  index: ReadonlyMap<string, string>,
): ThreadRow[] {
  const q = searchable(query.trim());
  return rows.filter((r) => {
    if (filter === "unread" && r.unread === 0) return false;
    if (filter === "awaiting" && !r.awaiting) return false;
    if (q === "") return true;
    return searchable(r.name).includes(q) || (index.get(r.clientId) ?? "").includes(q);
  });
}

/**
 * EACH CHIP HAS ITS OWN EMPTY SENTENCE — two of theirs have none and blank the column
 * (`docs/NEXT-WORK.md` W26, "five things of theirs we should not copy", #1).
 */
export function emptySentence(filter: InboxFilter, query: string, totalRows: number): string {
  const q = query.trim();
  if (q !== "") return `No conversation matches “${q}”.`;
  if (filter === "unread") return "Nothing unread.";
  if (filter === "awaiting") return "Nobody is waiting on a reply.";
  return totalRows === 0
    ? "No clients yet. Threads appear here once somebody accepts an invitation."
    : "No conversations.";
}

// ── The thread: one stamp per run, one `Seen`, the quote ───────────────────────────────────

/** Oldest first, by the string (ISO 8601 sorts as time); ties keep their arrival order. */
function oldestFirst(messages: readonly MessageRow[]): MessageRow[] {
  return [...messages].sort((a, b) => (a.createdAtISO < b.createdAtISO ? -1 : a.createdAtISO > b.createdAtISO ? 1 : 0));
}

/** The gap that ends a run — `ChatThreadView.showsTime`'s `> 300`, word for word. */
export const runGapSeconds = 300;

/**
 * The ids whose TIME is printed: where a run ENDS — the last message before the other side answers,
 * or before a gap of more than five minutes, and the last message of all.
 * ⚠️ PORTED FROM `ChatThreadView.swift`'s `showsTime(at:)`, NOT RE-DESIGNED: the two surfaces
 * stamping one conversation differently would be the vocabulary bug one level up. The harness gate
 * `w72-6` reads both and fails if the Swift's `> 300` or its sender test drifts from this file.
 * ⚠️ A TIMESTAMP THAT WILL NOT PARSE ALWAYS STAMPS — the gap to it is unknown, and an unknown gap is
 * not a short one.
 */
export function stampedIds(messages: readonly MessageRow[]): Set<string> {
  const list = oldestFirst(messages);
  const out = new Set<string>();
  list.forEach((m, i) => {
    const next = list[i + 1];
    if (next === undefined) { out.add(m.id); return; }
    if (next.senderId !== m.senderId) { out.add(m.id); return; }
    const gap = (Date.parse(next.createdAtISO) - Date.parse(m.createdAtISO)) / 1000;
    if (Number.isNaN(gap) || gap > runGapSeconds) out.add(m.id);
  });
  return out;
}

/**
 * The ONE message that carries a receipt: the coach's latest. `Seen` under every bubble is
 * theirs (Everfit stamps each one); ours says it once, where it answers "has he read what I said".
 */
/* ── HIS WORD 2026-10-15 (on William's item 6, *"Chat box could be cleaner"*): *"the messages of the coach with the clients are too
 * far from each other? and the days are not needed. if it's a different day ok mention it under the message where now we have the
 * time … look deep online what the most popular chat apps do like imessage, whatsapp, and all and decide."* LOOKED (10-15):
 * iMessage draws no per-message time — a day + time header on the first message of a day and after a lull, and runs of one sender
 * sit a pixel or two apart; WhatsApp and Telegram draw a day pill per day and the clock inside each bubble, runs tight; Messenger
 * as iMessage. DECIDED: the centred day heading goes; the stamp under a run's last message (the phone's five-minute rule, above)
 * carries the DAY only when it differs from the previous stamp's — `Today 17:00` · `Yesterday 21:01` · `Monday 03:02` (the last
 * six days) · `28 Sep 03:02` · `28 Sep 2025 03:02` — and a run of one sender sits tight. Days are the READER's calendar days
 * (a clock is local, so is its day). ── */

/** `Today` · `Yesterday` · a weekday within the last six days · `28 Sep` · `28 Sep 2025` — for the reader's local day `key`. */
export function dayWord(key: string, now: Date): string {
  const today = dayKeyOf(now);
  if (key === today) return "Today";
  if (key === dayKeyOf(new Date(now.getTime() - 86_400_000))) return "Yesterday";
  const d = realDay(key);
  if (d === null) return key;
  if (key > today) return dayMonth(key);
  if (key >= dayKeyOf(new Date(now.getTime() - 6 * 86_400_000))) return WEEKDAYS[d.getUTCDay()] ?? key;
  return key.slice(0, 4) === today.slice(0, 4) ? dayMonth(key) : `${dayMonth(key)} ${key.slice(0, 4)}`;
}

/**
 * The words under a STAMPED message (`stampedIds`) where the DAY changes from the previous stamp's (and on the thread's
 * first stamp): `Day time`. Since his 10-15 word, no other message carries a stamp. `clock` is the screen's own local formatter, so the words are testable.
 */
export function stampLines(messages: readonly MessageRow[], now: Date, clock: (iso: string) => string): Map<string, string> {
  const stamped = stampedIds(messages);
  const out = new Map<string, string>();
  let prevDay: string | null = null;
  for (const m of oldestFirst(messages)) {
    if (!stamped.has(m.id)) continue;
    const d = new Date(m.createdAtISO);
    const key = Number.isNaN(d.getTime()) ? null : dayKeyOf(d);
    const time = clock(m.createdAtISO);
    // HIS WORD 10-15 (*"do we really need day and time each message? or only when it's a different day? … heavy for the
    // eye"*): a stamp is drawn ONLY where the day changes — the first stamped message of each day carries `Day time`; the
    // rest carry none (their time is the bubble's tooltip). A time that will not parse still stamps (an unknown day).
    if (key === null) out.set(m.id, time);
    else if (key !== prevDay) out.set(m.id, `${dayWord(key, now)} ${time}`.trim());
    if (key !== null) prevDay = key;
  }
  return out;
}

/**
 * The ids that START a run: the first message, a change of sender, or the message after a DRAWN stamp (`stamped`, the
 * screen's `stampLines`) — the rows after them sit tight.
 * ⚠️ RE-AIMED 10-18 (his "still looks not right"): the five-minute gap broke a run while, since his 10-15 word, nothing is
 * drawn there — one sender's messages sat 2 px or 10 px apart for no reason the eye could see. A run breaks only where
 * something shows why.
 */
export function runStartIds(messages: readonly MessageRow[], stamped: ReadonlyMap<string, string>): Set<string> {
  const list = oldestFirst(messages);
  const out = new Set<string>();
  list.forEach((m, i) => {
    const prev = list[i - 1];
    if (prev === undefined || prev.senderId !== m.senderId || stamped.has(prev.id)) out.add(m.id);
  });
  return out;
}

export function receiptTargetId(messages: readonly MessageRow[], coachId: string): string | null {
  const list = oldestFirst(messages);
  for (let i = list.length - 1; i >= 0; i--) if (list[i]?.senderId === coachId) return list[i]?.id ?? null;
  return null;
}

export type Quote = { id: string; text: string; found: boolean };

/**
 * The line quoted above a reply — the original, one truncated line.
 * ⚠️ A QUOTE WHOSE ORIGINAL IS NOT IN THE THREAD SAYS SO (`found: false`) rather than vanishing:
 * `on delete set null` clears the pointer when a message is gone, so a non-null `replyTo` that is
 * not in the loaded thread is a load that missed it, and the reader must not be told nothing was quoted.
 */
export const quoteCap = 60;
export function quoteOf(message: Pick<MessageRow, "replyTo">, thread: readonly MessageRow[]): Quote | null {
  const id = message.replyTo ?? null;
  if (id === null) return null;
  const original = thread.find((m) => m.id === id);
  if (original === undefined) return { id, text: "An earlier message", found: false };
  return { id, text: threadPreview(original, quoteCap), found: true };
}

// ── Reactions — the six ────────────────────────────────────────────────────────────────────

/**
 * THE SIX, in the order they are drawn — the hold menu's row and the picker's "Your reactions". Since 2026-10-15 they are
 * no longer the only emoji a reaction may be (`20261015233000_reactions_any_emoji.sql`: any of `public.reaction_emoji`);
 * the phone's `MessageReaction.six` is the same six, compared by the harness gate `w72-6` on every run.
 */
export const reactionSet = ["👍", "❤️", "🔥", "💪", "👏", "😂"] as const;
export type ReactionRow = { messageId: string; userId: string; emoji: string };
export type ReactionChip = { emoji: string; count: number; mine: boolean };

/** The chips under one bubble. RE-AIMED 2026-10-15 (his word: the `+` after the six opens every emoji): ANY emoji is drawn —
 *  the six first in their order, then the rest by count, then by UTF-16 code units (the phone's `MessageReaction.chips`
 *  sorts the same way, so both platforms draw one order). The database keeps the column to REAL emoji
 *  (`coach_message_reactions_a_real_emoji` → `public.reaction_emoji`, the picker's own list). */
export function reactionChips(rows: readonly ReactionRow[], messageId: string, me: string): ReactionChip[] {
  const mine = rows.find((r) => r.messageId === messageId && r.userId === me)?.emoji ?? null;
  const counts = new Map<string, number>();
  for (const r of rows) if (r.messageId === messageId) counts.set(r.emoji, (counts.get(r.emoji) ?? 0) + 1);
  const six: readonly string[] = reactionSet;
  const rank = (e: string) => { const i = six.indexOf(e); return i === -1 ? six.length : i; };
  return [...counts]
    .sort(([a, na], [b, nb]) => rank(a) - rank(b) || nb - na || (a < b ? -1 : a > b ? 1 : 0))
    .map(([emoji, count]) => ({ emoji, count, mine: mine === emoji }));
}

/** HIS 10-16 WORD — WHICH REMOVAL A HELD MESSAGE OFFERS ("if it's not seen yet, to be deleted but if seen deleted only for you
 *  available"): my own message the other side has not seen → Unsend (gone for both — the database's `unsend_message` asks the
 *  same three facts again at the moment of the call); my seen message, or theirs → Delete for you; a review card → nothing.
 *  The phone's `MessageRemoval` is the twin (the harness executes both answers). */
export type MessageRemoval = "unsend" | "deleteForMe" | "none";
export function messageRemoval(m: Pick<MessageRow, "senderId" | "readAtISO" | "kind">, reader: string): MessageRemoval {
  if ((m.kind ?? "message") !== "message") return "none";
  return m.senderId === reader && m.readAtISO === null ? "unsend" : "deleteForMe";
}

export type ReactionWrite =
  | { kind: "add"; emoji: string }
  | { kind: "remove" }
  | { kind: "replace"; emoji: string };

/**
 * What tapping an emoji does — ONE per person per message, tap again removes (the table's primary key
 * is `(message_id, user_id)`, and it has no UPDATE grant, so a change is a remove then an add).
 */
export function reactionWrite(current: string | null, tapped: string): ReactionWrite {
  if (current === tapped) return { kind: "remove" };
  if (current === null) return { kind: "add", emoji: tapped };
  return { kind: "replace", emoji: tapped };
}

// ── The thread is ONLY MESSAGES (his 2026-10-09 word) ──────────────────────────────────────

/**
 * 🔴 HIS WORD, 2026-10-09: *"i hate this "✓ Trained · Mobility & Handstand Play · 43 sets · 06:28 PM" and so on in the
 * messages/chat. there needs to be just the chat man! minimal and simple, no extra info like that!"*
 * THE ONE RULE for what a thread draws — the phone's twin is `ChatRow.isMessage` (`ChatThread.swift`), the SAME cases on
 * both sides (vitest `messages.test.ts` + harness `w72-6-the-inbox` ruler 12). A row a PERSON wrote is a message: a plain
 * message, a review, a comment on it (folded into the review's bubble) — `coach_messages_kind`'s whole vocabulary — or a row
 * with no kind (written before the column, or the beta thread's). Anything else is a row the system DERIVED and never drawn
 * here: the W72 step-6 `Trained · … · N sets · time` line (derived from `set_logs`) is gone, its reader with it.
 * Where the coach still SEES who trained: the roster's week line, the client page's calendar, the Dashboard's queue.
 */
export function isMessage(kind: string | null | undefined): boolean {
  return kind === null || kind === undefined || kind === "message" || kind === "review" || kind === "comment";
}

export type TimelineItem =
  | { kind: "message"; message: MessageRow; mine: boolean; atISO: string }
  /** A review and its comments — ONE item (W82 step 4: ten comments never become ten bubbles). */
  | { kind: "review"; review: MessageRow; comments: MessageRow[]; replies: MessageRow[]; mine: boolean; atISO: string };
export type TimelineDay = { dayKey: string | null; items: TimelineItem[] };

/**
 * The conversation, oldest first, by day — only what was said (`isMessage`). A day holds a separator only because a
 * message was written on it: with no derived rows, no day line ever stands over nothing.
 * ⚠️ `conversation()`'s rule for an unreadable timestamp stands: it gets its own undated block,
 * never a day it was not sent on.
 */
export function timeline(
  messages: readonly MessageRow[],
  readerId: string,
  dayKeyOf: (iso: string) => string | null,
): TimelineDay[] {
  const items: TimelineItem[] = foldReviews(messages.filter((m) => isMessage(m.kind))).map((f): TimelineItem => f.kind === "review"
    ? { kind: "review", review: f.review, comments: [...f.comments], replies: [...f.replies], mine: f.review.senderId === readerId, atISO: f.latestISO }
    : { kind: "message", message: f.message, mine: f.message.senderId === readerId, atISO: f.message.createdAtISO })
    .sort((a, b) => (a.atISO < b.atISO ? -1 : a.atISO > b.atISO ? 1 : 0));
  const days: TimelineDay[] = [];
  for (const it of items) {
    const key = dayKeyOf(it.atISO);
    const last = days[days.length - 1];
    if (last !== undefined && last.dayKey === key && key !== null) last.items.push(it);
    else days.push({ dayKey: key, items: [it] });
  }
  return days;
}

/**
 * The time on the right of a thread row at desktop width — `14:02` today, `Tue` this past week, `12 Sep` before
 * that (Proposal 6's still). ⚠️ THE PHONE WIDTH KEEPS `messageAge` (`2 hr`), W21's reading of the app's own row;
 * the two spans are mutually exclusive by width, like the title's. A date that will not parse is an empty string
 * (the preview already says `No messages yet`), never a dash where a clock goes.
 */
const ROW_WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const ROW_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;
export function threadTime(
  iso: string | null,
  todayKey: string,
  dayKeyOf: (iso: string) => string | null,
  clockOf: (iso: string) => string,
): string {
  if (iso === null || iso === "") return "";
  const k = dayKeyOf(iso);
  if (k === null) return "";
  if (k >= todayKey) return clockOf(iso);
  const d = new Date(`${k}T00:00:00Z`), t = new Date(`${todayKey}T00:00:00Z`);
  const days = Math.round((t.getTime() - d.getTime()) / 86_400_000);
  if (days < 7) return ROW_WEEKDAYS[d.getUTCDay()] ?? "";
  return `${String(d.getUTCDate())} ${ROW_MONTHS[d.getUTCMonth()] ?? ""}`;
}
